import React, { useEffect, useState, useRef } from "react";
import { FiEdit, FiUser, FiMapPin } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { base_url } from "../utils/axiosConfig";

const Profile = () => {
  const fileInputRef = useRef();

  const getTokenFromLocalStorage = localStorage.getItem("user_website")
    ? JSON.parse(localStorage.getItem("user_website"))
    : null;

  const config2 = {
    headers: {
      Authorization: `Bearer ${
        getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
      }`,
    },
  };

  const [user, setUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  /* ---------------- Validation ---------------- */
  const editProfileSchema = Yup.object({
    firstname: Yup.string().required("First name required"),
    lastname: Yup.string().required("Last name required"),
    email: Yup.string().email("Invalid email").required("Email required"),
    mobile: Yup.string().required("Mobile required"),
    dob: Yup.string().required("DOB required"),
    gender: Yup.string().required("Gender required"),
    bloodGroup: Yup.string().required("Blood group required"),
    address: Yup.object({
      address_line: Yup.string().required("Address required"),
      city: Yup.string().required("City required"),
      state: Yup.string().required("State required"),
      country: Yup.string().required("Country required"),
      pincode: Yup.string().required("Pincode required"),
    }),
  });

  /* ---------------- Fetch Profile ---------------- */
  useEffect(() => {
    const fetchUser = async () => {
      const res = await fetch(`${base_url}v1/user/getEndUserProfile`, {
        headers: config2.headers,
      });
      const { data } = await res.json();
      setUser(data);
    };
    fetchUser();
  }, []);

  /* ---------------- Update Profile ---------------- */
  const handleEditProfile = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();

      Object.keys(values).forEach((key) => {
        if (key !== "address") formData.append(key, values[key]);
      });

      formData.append("address_line", values.address.address_line);
      formData.append("city", values.address.city);
      formData.append("state", values.address.state);
      formData.append("country", values.address.country);
      formData.append("pincode", values.address.pincode);

      if (values.profilePhoto) {
        formData.append("profilePhoto", values.profilePhoto);
      }

      const res = await fetch(`${base_url}v1/user/updateEndsUserProfile`, {
        method: "POST",
        headers: { Authorization: config2.headers.Authorization },
        body: formData,
      });

      const { data } = await res.json();
      setUser(data);
      toast.success("Profile Updated Successfully");
      setShowEditModal(false);
    } catch (error) {
      toast.error("Update Failed");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- Input Style ---------------- */
  const inputStyle = (error, touched) =>
    `w-full border rounded-lg px-3 py-2 outline-none transition
     ${error && touched ? "border-red-500" : "border-gray-300"}
     focus:border-[#35A93E] focus:ring-1 focus:ring-[#35A93E]`;

  if (!user) return <div className="text-center mt-20">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 py-6">
      <div className="max-w-6xl mx-auto px-4">
        {/* PROFILE HEADER */}
        <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col md:flex-row items-center gap-6">
          <div className="relative">
            <img
              src={previewImage || user.profilePhoto || "/default-user.png"}
              alt="Profile"
              className="w-28 h-28 rounded-full object-cover border-4 border-[#35A93E]"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="absolute bottom-0 right-0 bg-[#35A93E] p-2 rounded-full text-white"
            >
              <FiEdit size={14} />
            </button>
          </div>

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold">
              {user.firstname} {user.lastname}
            </h2>
            <p className="text-gray-500">{user.email}</p>
            <p className="text-gray-500">{user.mobile}</p>
            <span className="mt-2 inline-block bg-green-100 text-[#35A93E] px-3 py-1 rounded-full text-sm">
              Blood Group: {user.bloodGroup || "N/A"}
            </span>
          </div>

          <button
            onClick={() => setShowEditModal(true)}
            className="bg-[#35A93E] text-white px-5 py-2 rounded-lg hover:bg-[#2d8a33]"
          >
            Edit Profile
          </button>
        </div>

        {/* INFO CARDS */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div className="bg-white p-5 rounded-xl shadow-sm">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <FiUser /> Personal Information
            </h3>
            <p>
              <b>DOB:</b> {user.dob?.split("T")[0]}
            </p>
            <p>
              <b>Gender:</b> {user.gender}
            </p>
          </div>

          <div className="bg-white p-5 rounded-xl shadow-sm">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <FiMapPin /> Address
            </h3>
            <p>{user.address?.address_line}</p>
            <p>
              {user.address?.city}, {user.address?.state}
            </p>
            <p>
              {user.address?.country} - {user.address?.pincode}
            </p>
          </div>
        </div>

        {/* EDIT MODAL */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          centered
          size="lg"
        >
          <Modal.Body className="p-6">
            <Formik
              initialValues={{
                firstname: user.firstname || "",
                lastname: user.lastname || "",
                email: user.email || "",
                mobile: user.mobile || "",
                dob: user.dob?.split("T")[0] || "",
                gender: user.gender || "",
                bloodGroup: user.bloodGroup || "",
                profilePhoto: null,
                address: {
                  address_line: user.address?.address_line || "",
                  city: user.address?.city || "",
                  state: user.address?.state || "",
                  country: user.address?.country || "",
                  pincode: user.address?.pincode || "",
                },
              }}
              validationSchema={editProfileSchema}
              onSubmit={handleEditProfile}
              enableReinitialize
            >
              {({ errors, touched, setFieldValue, isSubmitting }) => (
                <Form className="grid grid-cols-2 gap-4">
                  <div>
                    <Field
                      name="firstname"
                      className={inputStyle(
                        errors.firstname,
                        touched.firstname,
                      )}
                      placeholder="First Name"
                    />
                    {errors.firstname && touched.firstname && (
                      <div className="text-red-500 text-sm">
                        {errors.firstname}
                      </div>
                    )}
                  </div>

                  <div>
                    <Field
                      name="lastname"
                      className={inputStyle(errors.lastname, touched.lastname)}
                      placeholder="Last Name"
                    />
                    {errors.lastname && touched.lastname && (
                      <div className="text-red-500 text-sm">
                        {errors.lastname}
                      </div>
                    )}
                  </div>

                  <div>
                    <Field
                      name="email"
                      className={inputStyle(errors.email, touched.email)}
                      placeholder="Email"
                    />
                    {errors.email && touched.email && (
                      <div className="text-red-500 text-sm">{errors.email}</div>
                    )}
                  </div>

                  <div>
                    <Field
                      name="mobile"
                      className={inputStyle(errors.mobile, touched.mobile)}
                      placeholder="Mobile"
                    />
                    {errors.mobile && touched.mobile && (
                      <div className="text-red-500 text-sm">
                        {errors.mobile}
                      </div>
                    )}
                  </div>

                  <div>
                    <Field
                      type="date"
                      name="dob"
                      className={inputStyle(errors.dob, touched.dob)}
                    />
                  </div>

                  <div>
                    <Field
                      as="select"
                      name="gender"
                      className={inputStyle(errors.gender, touched.gender)}
                    >
                      <option value="">Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Field>
                  </div>

                  <div className="col-span-2">
                    <Field
                      name="bloodGroup"
                      className={inputStyle(
                        errors.bloodGroup,
                        touched.bloodGroup,
                      )}
                      placeholder="Blood Group"
                    />
                  </div>

                  <div className="col-span-2">
                    <Field
                      name="address.address_line"
                      className={inputStyle(
                        errors.address?.address_line,
                        touched.address?.address_line,
                      )}
                      placeholder="Address Line"
                    />
                  </div>

                  <div>
                    <Field
                      name="address.city"
                      className={inputStyle(
                        errors.address?.city,
                        touched.address?.city,
                      )}
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <Field
                      name="address.state"
                      className={inputStyle(
                        errors.address?.state,
                        touched.address?.state,
                      )}
                      placeholder="State"
                    />
                  </div>

                  <div>
                    <Field
                      name="address.country"
                      className={inputStyle(
                        errors.address?.country,
                        touched.address?.country,
                      )}
                      placeholder="Country"
                    />
                  </div>

                  <div>
                    <Field
                      name="address.pincode"
                      className={inputStyle(
                        errors.address?.pincode,
                        touched.address?.pincode,
                      )}
                      placeholder="Pincode"
                    />
                  </div>

                  <div className="col-span-2">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setFieldValue("profilePhoto", file);
                        setPreviewImage(URL.createObjectURL(file));
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="col-span-2 bg-[#35A93E] text-white py-2 rounded-lg hover:bg-[#2d8a33]"
                  >
                    Save Profile
                  </button>
                </Form>
              )}
            </Formik>
          </Modal.Body>
        </Modal>

        <ToastContainer />
      </div>
    </div>
  );
};

export default Profile;
